
import gql from "graphql-tag";
import {LocationFragment} from "./Location";
import {VeriflytResultFragment} from "./Veriflyt";
import {IsnResultWithHeaderFragment} from "./Isn";
import {PaxNodeComplianceAuthorizationFieldsFragment, PaxNodeComplianceFieldsFragment} from "./Compliance";

export const ScheduledPaxNodeFragment = gql`
fragment ScheduledPaxNodeFragment on PaxNode {
    _id
    _rev
    createdBy
    createdTs
    modifiedTs
    modifiedBy
    lastName
    firstName
    transitType
    scheduledOrder
    scheduledGroup
    classType
    paxWeight
    bagWeight
    bagCount
    chargeCode
    notesForPilot
    personID {
        _id
        dob
        nation
        extraBroadState
        credentials {
            _id
            key
            type
            expiration
        }
        contactInfo {
            phone {
                number
                type
            }
        }
        lastPaxWeight
        lastBagWeight
        lastBagCount
    }
    customerID {
        _id
        name
    }
    tpID {
        _id
        name
    }
    employerID {
        _id
        name
    }
    contractorID
    contractorDoc {
        _id
        name
    }
    departureID {
        ...LocationFragment
    }
    destinationID {
        ...LocationFragment
    }
    lastKnownController {
        ...LocationFragment
    }
    currentCarrierID {
        _id
        desig
        state
    }
    authorization {
        onWhiteList,
        ...PaxNodeComplianceAuthorizationFieldsFragment
    }
    complianceCheckError {
        message
    }
    requestedCheckInTime
    lastCheckInNotificationEvent {
        _id
        success
        createdTs
        errorMessage
    }
    ...PaxNodeComplianceFieldsFragment
}
${LocationFragment}
${PaxNodeComplianceFieldsFragment}
${PaxNodeComplianceAuthorizationFieldsFragment}
`

export const ScheduledCgoNodeFragment = gql`
fragment ScheduledCgoNodeFragment on CargoNode {
    _id
    _rev
    createdBy
    createdTs
    modifiedTs
    modifiedBy
    name
    classType
    transitType
    scheduledOrder
    scheduledGroup
    weight
    hazmat
    hazmatUnCode
    departureID {
        ...LocationFragment
    }
    lastKnownController {
        ...LocationFragment
    }
    destinationID {
        ...LocationFragment
    }
    currentCarrierID {
        _id
        desig
        state
    }
    attentionTo
    initials
    notes
    notesForPilot
    customerID {
        _id
        name
    }
    tpID {
        _id
        name
    }
}
${LocationFragment}
`

export const ScheduledFlightFragment = gql`
fragment ScheduledFlightFragment on FlightNode {
    _id
    createdBy
    createdTs
    modifiedTs
    modifiedBy
    desig
    scheduledFlightDate
    callSign
    state
    liveEditedState
    lastKnownController {
        ...LocationFragment
    }
    paxIDList {
        ...ScheduledPaxNodeFragment
    }
    cgoIDList {
        ...ScheduledCgoNodeFragment
    }
    pilot {
        _id
        name {
            lastName
            firstName
        }
    }
    copilot {
        _id
        name {
            lastName
            firstName
        }
    }
    legs
    legsArray {
        departure
        destination
        departureID
        destinationID
        bagCount
        bagWeight
        cgoCount
        cgoWeight
        order
        paxWeight
        paxCount
        cgoIDs{
            ...ScheduledCgoNodeFragment
        }
        paxIDs {
            ...ScheduledPaxNodeFragment
        }
    }
    tpID {
        _id
        name
    }
    customerID {
        _id
        name
    }
    contract {
        _id
        name
    }
    aircraftID {
        _id
        tailNum
    }
    departureID {
        ...LocationFragment
    }
    estDepartureTime
}
${ScheduledPaxNodeFragment}
${ScheduledCgoNodeFragment}
${LocationFragment}
`

export const ScheduledPaxCgoGroupV2Fragment = gql`
fragment ScheduledPaxCgoGroupV2Fragment on ScheduledPaxCgoGroupV2 {
    scheduledFlightDate
    scheduledGroup
    lastKnownController {
        _id
        name
    }
    paxOutbound
    paxTransfer
    paxInbound
    cgoOutbound
    cgoTransfer
    cgoInbound
    paxOutboundOnFlight
    paxTransferOnFlight
    paxInboundOnFlight
    cgoOutboundOnFlight
    cgoTransferOnFlight
    cgoInboundOnFlight
    legCount
    legs {
        departureID
        destinationID
        departureDoc {
            ...LocationFragment
        }
        destinationDoc {
            ...LocationFragment
        }
        scheduledOrder
        paxnodeIDs
        cgonodeIDs
        paxnodeDocs {
            ...ScheduledPaxNodeFragment
        }
        cgonodeDocs {
            ...ScheduledCgoNodeFragment
        }
        totalWeight
        totalPaxWeight
        totalCgoWeight
        totalBagWeight
        totalBagCount
    }
}
${ScheduledPaxNodeFragment}
${ScheduledCgoNodeFragment}
${LocationFragment}
`

export const ResolveScheduledPaxNodeIdsQuery = gql`
query ResolveScheduledPaxNodeIds($ids: [ID!]!){
    resolve_entity_ids(ids: $ids, typename: "PaxNode"){
        ...ScheduledPaxNodeFragment
    }
}
${ScheduledPaxNodeFragment}
`

export const ResolveScheduledCgoNodeIdsQuery = gql`
    query ResolveScheduledCgoNodeIds($ids: [ID!]!){
        resolve_entity_ids(ids: $ids, typename: "CargoNode"){
            ...ScheduledCgoNodeFragment
        }
    }
    ${ScheduledCgoNodeFragment}
`

export const Phase1StepQuery = gql`
    query Phase1StepQuery {
        getPhase1Step @client
    }
`

export const ScheduleHistoryListItemFragment = gql`
    fragment ScheduleHistoryListItemFragment on ScheduleHistoryListItem {
        groupName
        date
        createdBy
        lastKnownController
        paxInboundCount
        paxOutboundCount
        paxTransferCount
        paxCount
    }
`

export const ScheduleHistoryDetailFragment = gql`
    fragment ScheduleHistoryDetailFragment on ScheduleHistoryDetail {
        groupName
        date
        createdBy
        lastKnownController
        paxInboundCount
        paxOutboundCount
        paxTransferCount
        paxList {
            ...ScheduledPaxNodeFragment
        }
    }
    ${ScheduledPaxNodeFragment}
`

export const QueryGetPhase1ListDateRange = gql`
    query GetPhase1ListDateRange {
        getPhase1ListDateRange @client
    }
`
